'use client';
import React from "react";
import logger from "../services/logger";

/**
 * ErrorBoundary qui swallow l'erreur connue "t is not a function".
 *
 * Bug : un useEffect cleanup quelque part dans l'arbre throw lors de
 * certains démontages React 18. La stack ne pointe que sur react-dom
 * minifié, impossible d'identifier le composant source.
 *
 * Stratégie : on tente N fois de swallow + re-render. Si ça boucle
 * (le re-render re-throw immédiatement), on arrête et on laisse remonter.
 *
 * Utilisé dans les thèmes Ummah et Campagne (les plus anciens).
 * Travel et 2026 n'ont pas (encore ?) ce bug.
 */
const TARGET = /t is not a function/i;
const MAX_RETRIES = 2;

export default class CrashReloader extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, retries: 0 };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error) {
        const msg = error?.message || "";

        // Erreur inconnue → on laisse remonter (render fera throw).
        if (!TARGET.test(msg)) return;

        // Trop de retries sur la même page → on abandonne, laisse remonter.
        if (this.state.retries >= MAX_RETRIES) {
            logger.warn("[CrashReloader] max retries atteint, laisse remonter");
            return;
        }

        // Erreur connue → reset + incrément compteur pour re-render.
        this.setState(prev => ({
            error: null,
            retries: prev.retries + 1,
        }));
    }

    componentDidUpdate(prevProps) {
        // Nouvelle page → reset complet (error + compteur).
        if (prevProps.pageKey !== this.props.pageKey) {
            if (this.state.error || this.state.retries > 0) {
                this.setState({ error: null, retries: 0 });
            }
        }
    }

    render() {
        // Erreur connue bénigne (et on a encore des retries dispo)
        // → null pendant le tick avant reset.
        if (this.state.error && TARGET.test(this.state.error.message || "")
            && this.state.retries < MAX_RETRIES) {
            return null;
        }
        // Vraie erreur (inconnue OU retries épuisés) → on laisse remonter.
        if (this.state.error) {
            throw this.state.error;
        }
        return this.props.children;
    }
}
